import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    HostListener,
    NgZone,
    OnInit,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from 'app/core/auth/auth.service';
import {
    filter,
    fromEvent,
    interval,
    merge,
    switchMap,
    take,
    tap,
    timer,
} from 'rxjs';

const COUNTDOWN_SECONDS = 600;
const YELLOW_THRESHOLD = 300;
const RED_THRESHOLD = 60;
const ACTIVITY_RESUME_DELAY_MS = 300;
const BACKSTOP_CHECK_MS = 15 * 60 * 1000;
const DEPLOY_CHANNEL_NAME = 'deploy-version';
const VERSION_AVAILABLE_MESSAGE = 'version-available';
const UPDATING_MESSAGE = 'updating';
const SIGN_IN_PATH = '/sign-in';

type DeployBannerPhase = 'green' | 'yellow' | 'red';

interface DeployBannerPhaseStyle {
    icon: string;
    title: string;
    lead: string;
    container: string;
    text: string;
    accent: string;
    button: string;
    progress: string;
}

const PHASE_STYLES: Record<DeployBannerPhase, DeployBannerPhaseStyle> = {
    green: {
        icon: 'system_update_alt',
        title: 'Nueva versión disponible.',
        lead: ' Guarde su trabajo — la sesión se cerrará en ',
        container:
            'bg-green-500/20 border-green-500 dark:bg-green-400/10 dark:border-green-400/60',
        text: 'text-green-900 dark:text-green-200',
        accent: 'text-green-700 dark:text-green-400',
        button: 'border-green-700 text-green-800 dark:border-green-400 dark:text-green-300',
        progress: 'bg-green-600 dark:bg-green-400',
    },
    yellow: {
        icon: 'warning_amber',
        title: 'La sesión se cerrará pronto.',
        lead: ' Guarde su trabajo — cierre en ',
        container:
            'bg-amber-500/20 border-amber-500 dark:bg-amber-400/10 dark:border-amber-400/60',
        text: 'text-amber-900 dark:text-amber-200',
        accent: 'text-amber-700 dark:text-amber-400',
        button: 'border-amber-700 text-amber-800 dark:border-amber-400 dark:text-amber-300',
        progress: 'bg-amber-600 dark:bg-amber-400',
    },
    red: {
        icon: 'error_outline',
        title: 'Cierre de sesión inminente.',
        lead: ' Se cerrará en ',
        container:
            'bg-red-500/20 border-red-500 dark:bg-red-400/10 dark:border-red-400/60',
        text: 'text-red-900 dark:text-red-200',
        accent: 'text-red-700 dark:text-red-400',
        button: 'border-red-700 text-red-800 dark:border-red-400 dark:text-red-300',
        progress: 'bg-red-600 dark:bg-red-400',
    },
};

@Component({
    selector: 'deploy-banner',
    templateUrl: './deploy-banner.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgClass, MatButtonModule, MatIconModule],
})
export class DeployBannerComponent implements OnInit {
    private readonly _authService = inject(AuthService);
    private readonly _ngZone = inject(NgZone);
    private readonly _destroyRef = inject(DestroyRef);

    readonly visible = signal<boolean>(false);
    readonly remaining = signal<number>(COUNTDOWN_SECONDS);
    readonly paused = signal<boolean>(false);

    readonly phaseStyle = computed<DeployBannerPhaseStyle>(() => {
        const remaining: number = this.remaining();
        if (remaining <= RED_THRESHOLD) return PHASE_STYLES.red;
        if (remaining <= YELLOW_THRESHOLD) return PHASE_STYLES.yellow;
        return PHASE_STYLES.green;
    });

    readonly progressPct = computed<number>(
        () => (this.remaining() / COUNTDOWN_SECONDS) * 100
    );

    readonly formattedTime = computed<string>(() => {
        const minutes: number = Math.floor(this.remaining() / 60);
        const seconds: number = this.remaining() % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    });

    private _interval: ReturnType<typeof setInterval> | null = null;
    private _channel: BroadcastChannel | null = null;
    private _deadline: number = 0;
    private _lastTick: number = 0;

    ngOnInit(): void {
        this.openChannel();

        this._destroyRef.onDestroy(() => {
            this.stopCountdown();
            this._channel?.close();
        });

        this._authService.newVersionAvailable$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((available: boolean) => {
                if (!available) return;

                this._channel?.postMessage(VERSION_AVAILABLE_MESSAGE);
                this.show();
            });

        merge(
            fromEvent(document, 'visibilitychange'),
            interval(BACKSTOP_CHECK_MS)
        )
            .pipe(
                filter(
                    () =>
                        !this.visible() &&
                        document.visibilityState === 'visible'
                ),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe(() => this._authService.checkDeployVersion());
    }

    update(): void {
        this.stopCountdown();
        this._channel?.postMessage(UPDATING_MESSAGE);

        this._authService
            .signOut()
            .pipe(take(1))
            .subscribe(() => this.reloadOnSignIn());
    }

    @HostListener('window:beforeunload')
    onBeforeUnload(): void {
        if (this.visible()) this._authService.clearStorageForNewVersion();
    }

    private reloadOnSignIn(): void {
        this._authService.clearStorageForNewVersion();
        window.location.assign(SIGN_IN_PATH);
    }

    private openChannel(): void {
        if (typeof BroadcastChannel === 'undefined') return;

        this._channel = new BroadcastChannel(DEPLOY_CHANNEL_NAME);
        this._channel.onmessage = (event: MessageEvent) => {
            this._ngZone.run(() => {
                if (event.data === UPDATING_MESSAGE) {
                    window.location.reload();
                    return;
                }

                if (event.data === VERSION_AVAILABLE_MESSAGE) this.show();
            });
        };
    }

    private show(): void {
        if (this.visible()) return;

        this.visible.set(true);
        this.startCountdown();
        this.listenForUserActivity();
    }

    private startCountdown(): void {
        this._deadline = Date.now() + COUNTDOWN_SECONDS * 1000;
        this._lastTick = Date.now();

        this._ngZone.runOutsideAngular(() => {
            this._interval = setInterval(() => this.tick(), 1000);
        });
    }

    private tick(): void {
        const now: number = Date.now();
        if (this.paused()) this._deadline += now - this._lastTick;
        this._lastTick = now;

        const remaining: number = Math.max(
            0,
            Math.round((this._deadline - now) / 1000)
        );
        if (remaining === this.remaining()) return;

        this.remaining.set(remaining);

        if (remaining <= 0) {
            this._ngZone.run(() => this.update());
        }
    }

    private stopCountdown(): void {
        if (this._interval === null) return;

        clearInterval(this._interval);
        this._interval = null;
    }

    private listenForUserActivity(): void {
        merge(fromEvent(document, 'keydown'), fromEvent(document, 'input'))
            .pipe(
                tap(() => this.paused.set(true)),
                switchMap(() => timer(ACTIVITY_RESUME_DELAY_MS)),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe(() => this.paused.set(false));
    }
}
